import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import styles from './styles';
import { Box, Button, FormControlLabel, IconButton, Switch, Tabs, Typography } from '@mui/material';
import { ContentWidget } from './components';
import { TabSettings } from '../../../../components/Dialogs/TabSettings';
import { PlusIcon } from '../../../../assets/icons';
import {Check, Close, Edit, Delete, Undo, Redo, Settings } from '@mui/icons-material';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import { ITheme } from '../../../../resource';
import { useAlertContext } from '../../../../components/Alert/alertContext';

type ITab = {
  id: number;
  label: string;
  content?: any;
};

interface IWidgetProps {
  isin?: string;
  widget?: any;
  originalWidget?: any;
  preview?: boolean;
  theme?: ITheme | null;
  themeColor?: string;
  onChangeWidget?: (widget) => void;
}

interface HistorySnapshot {
  tabs: ITab[];
  widgetContent: any;
  mode: string;
  activeTab: number | null;
}

const deepCopy = <T,>(value: T): T => {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
};

const Widget: FC<IWidgetProps> = ({ isin, widget, originalWidget, preview, theme: widgetTheme, themeColor, onChangeWidget }) => {
  const { activeAlert } = useAlertContext();

  // When widget has tabs
  const [tabs, setTabs] = useState<ITab[]>(widget?.tabs?.length ? widget.tabs : []);

  // When widget has content without tabs - use first tab's content if mode is single and tabs exist
  const getInitialWidgetContent = () => {
    if (widget?.mode === 'single' && widget?.tabs?.length > 0) {
      return widget.tabs[0]?.content || [];
    }
    return widget?.contents || [];
  };
  const [widgetContent, setWidgetContent] = useState<any>(getInitialWidgetContent());

  const [newTab, setNewTab] = useState<ITab | null>(null);
  const [editingTab, setEditingTab] = useState<boolean>(false);
  const [editingTabTitle, setEditingTabTitle] = useState<string>('');
  const [activeTab, setActiveTab] = useState<number | null>(null);
  const [mode, setMode] = useState<string>(widget?.mode || 'tab');
  const [isFragment, setIsFragment] = useState<boolean>(widget?.isFragment || false);
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const [history, setHistory] = useState<{ past: HistorySnapshot[]; future: HistorySnapshot[] }>({ past: [], future: [] });
  const isRestoringRef = useRef<boolean>(false);

  const createSnapshot = (): HistorySnapshot => ({
    tabs: deepCopy(tabs),
    widgetContent: deepCopy(widgetContent),
    mode,
    activeTab,
  });

  const pushHistory = (): void => {
    if (isRestoringRef.current) return;
    const snapshot = createSnapshot();
    setHistory((h) => ({ past: [...h.past, snapshot], future: [] }));
  };

  const applySnapshot = (snapshot: HistorySnapshot): void => {
    isRestoringRef.current = true;
    setTabs(snapshot.tabs);
    setWidgetContent(snapshot.widgetContent);
    setMode(snapshot.mode);
    setActiveTab(snapshot.activeTab);
    if (onChangeWidget) {
      onChangeWidget({
        ...widget,
        tabs: snapshot.tabs,
        contents: snapshot.widgetContent,
        mode: snapshot.mode,
      });
    }
    setTimeout(() => { isRestoringRef.current = false; }, 0);
  };

  const canUndo = history.past.length > 0;
  const canRedo = history.future.length > 0;

  const handleUndo = (): void => {
    if (!canUndo) return;
    setHistory((h) => {
      const past = [...h.past];
      const last = past.pop() as HistorySnapshot;
      const current = createSnapshot();
      applySnapshot(last);
      return { past, future: [current, ...h.future] };
    });
  };

  const handleRedo = (): void => {
    if (!canRedo) return;
    setHistory((h) => {
      const [next, ...rest] = h.future;
      const current = createSnapshot();
      applySnapshot(next);
      return { past: [...h.past, current], future: rest };
    });
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isTyping = !!target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        (target.getAttribute && target.getAttribute('contenteditable') === 'true')
      );
      if (isTyping) return;
      if (e.ctrlKey && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        handleUndo();
      }
      if (e.ctrlKey && (e.key === 'y' || e.key === 'Y')) {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [history]);

  const handleChangeActiveTab = (_, value) => {
    setActiveTab(value);
  };

  const handleAddNewTab = () => {
    setNewTab({ id: +new Date(), label: '', content: [] });
    setActiveTab(null);
  };

  const handleChangeNewTab = (e: ChangeEvent<HTMLInputElement>) => {
    if (newTab) {
      setNewTab({ ...newTab, label: e.target.value });
    }
  };

  const handleConfirmAddTab = () => {
    if (newTab && onChangeWidget) {
      pushHistory();
      const updatedTabs = [ ...tabs, newTab];
      setTabs(updatedTabs);
      onChangeWidget({
        ...widget,
        tabs: updatedTabs,
        mode,
        isFragment
      });
      setNewTab(null);
    }
  };

  const handleCancelAddNewTab = () => {
    setNewTab(null);
  };

  const handleChangeTabContent = (id: number, content: any) => {
    if (onChangeWidget) {
      pushHistory();
      if (mode === 'tab') {
        const updatedTabs = tabs.map((tab) => tab.id === id ? { ...tab, content } : tab);
        setTabs(updatedTabs);
        onChangeWidget({
          ...widget,
          tabs: updatedTabs,
          contents: null,
          mode: mode,
          isFragment
        });
      } else {
        // Single view mode - update widgetContent directly
        setWidgetContent(content);
        // Also update first tab's content if tabs exist
        if (tabs.length > 0) {
          const updatedTabs = tabs.map((tab, index) => index === 0 ? { ...tab, content } : tab);
          setTabs(updatedTabs);
          onChangeWidget({
            ...widget,
            tabs: updatedTabs,
            contents: content,
            mode: mode,
            isFragment
          });
        } else {
          onChangeWidget({
            ...widget,
            contents: content,
            mode: mode,
            isFragment
          });
        }
      }
    }
  };

  const handleRemoveTab = () => {
    activeAlert({
      type: 'info',
      text: "Are you sure to remove this tab?",
    }, () => {
      pushHistory();
      const updatedTabs = tabs.filter((tab) => tab.id !== activeTab);
      setTabs(updatedTabs);
      if (onChangeWidget) {
        onChangeWidget({
          ...widget,
          contents: null,
          tabs: updatedTabs,
          mode,
          isFragment
        });
        
        setActiveTab(null);
      }
    });
  };

  const handleEditTabTitle = () => {
    if (editingTab) {
      pushHistory();
      const updatedTabs = tabs.map((tab) => tab.id === activeTab ? { ...tab, label: editingTabTitle } : tab);
      setTabs(updatedTabs);
      if (onChangeWidget) {
        onChangeWidget({
          ...widget,
          tabs: updatedTabs,
          contents: null,
          mode,
          isFragment
        });
      }
    } else {
      setEditingTabTitle(tabs.find((tab) => tab.id === activeTab)?.label || "");
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
    setEditingTab(!editingTab);
  };

  const handleChangeTabTitle = (e) => {
    setEditingTabTitle(e.target.value);
  };

  const handleCancelEditing = () => {
    setEditingTab(false);
    setEditingTabTitle("");
  };

  const onChangeViewMode = () => {
    if (onChangeWidget) {
      const updatedMode = mode === 'tab' ? 'single' : 'tab';
      pushHistory();
      setMode(updatedMode);
      
      // When switching to single view, use first tab's content
      if (updatedMode === 'single' && tabs.length > 0) {
        const firstTabContent = tabs[0]?.content || [];
        setWidgetContent(firstTabContent);
        onChangeWidget({
          ...widget,
          mode: updatedMode,
          contents: firstTabContent,
          isFragment
        });
      } else {
        onChangeWidget({
          ...widget,
          mode: updatedMode,
          isFragment
        });
      }
    }
  }

  const onToggleFragment = () => {
    if (onChangeWidget) {
      const updatedFragment = !isFragment;
      pushHistory();
      setIsFragment(updatedFragment);
      onChangeWidget({
        ...widget,
        isFragment: updatedFragment,
        mode
      });
    }
  }

  const handleOpenSettings = () => {
    setSettingsOpen(true);
  }

  const handleCloseSettings = () => {
    setSettingsOpen(false);
  }

  const onDragComplete = (result) => {
    if (!result.destination) return;

    const arr = [...tabs];

    const removedItem = arr.splice(result.source.index, 1)[0];
    arr.splice(result.destination.index, 0, removedItem);
    pushHistory();
    setTabs(arr);
    onChangeWidget && onChangeWidget({
      ...widget,
      tabs: arr,
      isFragment
    });
  };

  return (
    <Box sx={styles.widgetWrapper}>
      {/* <Box sx={styles.headerActions}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
          <IconButton size="medium" title="Undo (Ctrl+Z)" onClick={handleUndo} disabled={!canUndo}>
            <Undo />
          </IconButton>
          <IconButton size="medium" title="Redo (Ctrl+Y)" onClick={handleRedo} disabled={!canRedo}>
            <Redo />
          </IconButton>
        </Box>
      </Box> */}

      <Box sx={styles.widgetContainer}>
        {mode === 'tab' ? (
          <>
            <Tabs sx={(theme) => styles.tabBar(theme, widgetTheme?.colorSet?.background || themeColor)} variant='scrollable' value={activeTab} onChange={handleChangeActiveTab}>
              <DragDropContext onDragEnd={onDragComplete}>
                <Droppable droppableId="tabs-list" direction="horizontal">
                  {(provided) => (
                    <Box sx={{ display: "flex" }} ref={provided.innerRef} {...provided.droppableProps}>
                      {tabs?.map((tab, index) => (
                        <Draggable
                          key={tab.id}
                          draggableId={tab.id.toString()}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <Box
                              sx={{ height: '100%' }}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              key={index}
                            >
                              <Box
                                sx={(theme) => editingTab
                                  ? styles.editTab(theme, { color: widgetTheme?.colorSet?.background || themeColor })
                                  : styles.draggableTab(theme, {
                                    active: tab.id === activeTab,
                                    color: widgetTheme?.colorSet?.background || themeColor,
                                  }
                                )}
                                onClick={() => { setActiveTab(tab.id) }}
                              >
                                <Box display="flex" alignItems="center" sx={(editingTab && activeTab === tab.id) ? styles.editTabInput : styles.tabInput}>
                                  <Typography>{!(editingTab && activeTab === tab.id) && tab.label}</Typography>
                                  <input hidden={!(editingTab && activeTab === tab.id)} placeholder='Tab title' value={editingTabTitle} ref={inputRef} onChange={handleChangeTabTitle} />
                                  {activeTab === tab.id && (
                                    <IconButton
                                      sx={(theme) => styles.tabControlIcon(theme)}
                                      onClick={handleEditTabTitle}
                                    >
                                      {editingTab ? (<Check />) : (<Edit />)}
                                    </IconButton>
                                  )}
                                  {editingTab && activeTab === tab.id && (
                                    <IconButton
                                      sx={(theme) => styles.tabControlIcon(theme)}
                                      onClick={handleCancelEditing}
                                    >
                                      <Close />
                                    </IconButton>
                                  )}
                                  {!editingTab && activeTab === tab.id && (
                                    <IconButton
                                      sx={(theme) => styles.tabControlIcon(theme)}
                                      onClick={handleRemoveTab}
                                    >
                                      <Delete />
                                    </IconButton>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>
              {newTab && (
                <Box sx={styles.newTabInput}>
                  <input placeholder='New Tab' value={newTab.label} onChange={handleChangeNewTab} />
                  <IconButton disabled={!newTab.label} sx={styles.newTabIcon} onClick={handleConfirmAddTab}><Check /></IconButton>
                  <IconButton sx={styles.newTabIcon} onClick={handleCancelAddNewTab}><Close /></IconButton>
                </Box>
              )}
              <Button sx={styles.addNewTab} disabled={!!newTab} variant="text" onClick={handleAddNewTab} startIcon={<PlusIcon />}>
                Add
              </Button>
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                <IconButton 
                  size="small"
                  sx={styles.tabSettingsIcon}
                  onClick={handleOpenSettings}
                  title="Tab Settings"
                >
                  <Settings  />
                </IconButton>
              </Box>
            </Tabs>
            <Box sx={styles.tabContent}>
              {activeTab ? (
                <ContentWidget
                  id={activeTab}
                  isin={isin}
                  preview={preview}
                  isTop
                  type="container"
                  themeColor={themeColor}
                  content={tabs?.find((tab) => tab.id === activeTab)?.content}
                  originalContent={originalWidget?.tabs?.find(item => item.id === activeTab)?.content}
                  onChange={handleChangeTabContent}
                  onDelete={undefined}
                />
              ) : (
                <Typography color="lightgray">No Content</Typography>
              )}
            </Box>
          </>
        ) : (
          <Box>
            <Box sx={(theme) => styles.contentHeader(theme, themeColor)}>
              <Typography>Widget Content</Typography>
              <IconButton 
                size="small"
                sx={styles.tabSettingsIcon}
                onClick={handleOpenSettings}
                title="Tab Settings"
              >
                <Settings />
              </IconButton>
            </Box>
            <Box sx={styles.tabContent}>
              <ContentWidget
                id={0}
                isin={isin}
                preview={preview}
                type="container"
                // originalContent={or}
                content={widgetContent}
                themeColor={themeColor}
                onChange={handleChangeTabContent}
                onDelete={undefined}
              />
            </Box>
          </Box>
        )}
      </Box>

      <TabSettings
        open={settingsOpen}
        mode={mode}
        isFragment={isFragment}
        onClose={handleCloseSettings}
        onToggleMode={onChangeViewMode}
        onToggleFragment={onToggleFragment}
      />
    </Box>
  )
};

export default Widget;
